import { useState } from "react";
import { X, Gift } from "lucide-react";
import { format, parseISO } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";

type Contact = Tables<"contacts">;

interface ReminderBannerProps {
  contacts: Contact[];
  reminderLeadTime: string;
  getDaysUntilBirthday: (birthday: string) => number;
  onContactClick: (contact: Contact) => void;
}

const LEAD_TIME_DAYS: Record<string, number> = {
  "1_day": 1,
  "3_days": 3,
  "1_week": 7,
};

const ReminderBanner = ({ contacts, reminderLeadTime, getDaysUntilBirthday, onContactClick }: ReminderBannerProps) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const threshold = LEAD_TIME_DAYS[reminderLeadTime] ?? 3;
  const upcoming = contacts
    .filter((c) => getDaysUntilBirthday(c.birthday) <= threshold)
    .sort((a, b) => getDaysUntilBirthday(a.birthday) - getDaysUntilBirthday(b.birthday));

  if (upcoming.length === 0) return null;

  return (
    <div className="relative mb-6 overflow-hidden rounded-2xl border border-accent/30 bg-accent/10 p-4 animate-scale-in">
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-2 mb-3">
        <Gift className="h-4 w-4 text-accent" />
        <h3 className="text-sm font-semibold text-foreground">
          🎉 Upcoming {upcoming.length === 1 ? "Birthday" : "Birthdays"}
        </h3>
      </div>

      <div className="space-y-2">
        {upcoming.map((contact) => {
          const days = getDaysUntilBirthday(contact.birthday);
          return (
            <button
              key={contact.id}
              onClick={() => onContactClick(contact)}
              className="flex w-full items-center justify-between rounded-xl bg-card/60 px-3 py-2 text-left transition-colors hover:bg-card"
            >
              <div className="min-w-0">
                <span className="text-sm font-medium text-foreground">{contact.full_name}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {format(parseISO(contact.birthday), "MMM d")}
                </span>
              </div>
              <span className="ml-3 shrink-0 rounded-full bg-accent/20 px-2 py-0.5 text-xs font-semibold text-accent">
                {days === 0 ? "Today!" : days === 1 ? "Tomorrow" : `${days} days`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReminderBanner;
